import { useMemo, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { Page } from 'components/Page'
import { type GridColDef, type GridEventListener } from '@mui/x-data-grid'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { ErrorIndicator } from 'components/Shared/ErrorIndicator'
import { NoDataIndicator } from 'components/Shared/NoDataIndicator'
import { PageHeader } from 'components/Shared/PageHeader'
import { Colors, colorPrimitives } from 'components/Theme'
import { useNavigate } from 'react-router-dom'
import { GridSearchField } from 'components/GridSearchField/GridSearchField'
import {
  type PromotionStatus,
  calculatePromotionStatus,
  dataTablePromotionDateFormatter,
} from 'utils/util'
import { useLayout } from 'hooks/useLayout'
import { DataTable } from 'components/Shared/DataTable'
import { useCurrentCorporateAccountStore } from 'stores/useCurrentCorporateAccountStore'
import { useGetPromotionsByCorporateAccountId } from 'hooks/api/useGetPromotionsByCorporateAccountId'
import { type Promotion } from 'src/types/api.ts'
import { useSetContactUsFields } from 'stores/useSetContactUsFields'
import { getLEIdsByPermission, hasContactUsPermission } from 'utils/permissions'
import { useSetMe } from 'stores/useMeQuery'

export const PromotionsPage = () => {
  const selectedCorporateAccountId =
    useCurrentCorporateAccountStore().currentCorporateAccountId
  const navigate = useNavigate()
  const { passedMe } = useSetMe()
  const meQuery = passedMe
  const { isMobile } = useLayout()
  const promotionsQuery = useGetPromotionsByCorporateAccountId({
    corporateAccountId: Number(selectedCorporateAccountId),
  })

  const contactUsPermission = hasContactUsPermission()

  const columns = useMemo(
    (): GridColDef[] => [
      {
        field: 'title',
        headerName: 'Special & Event Name',
        minWidth: 380,
        flex: 1,
      },
      {
        field: 'startDate',
        headerName: 'Start Date',
        minWidth: 100,
        flex: 1,
        valueFormatter: dataTablePromotionDateFormatter(isMobile, 'Start Date'),
      },
      {
        field: 'endDate',
        headerName: 'End Date',
        minWidth: 100,
        flex: 1,
        valueFormatter: dataTablePromotionDateFormatter(isMobile, 'End Date'),
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 100,
        flex: 1,
        valueGetter: (params) => {
          const startDate = params.row.startDate
            ? new Date(params.row.startDate)
            : null
          const endDate = params.row.endDate
            ? new Date(params.row.endDate)
            : null

          const promoStatusLabel = (
            status: PromotionStatus,
            isMobile: boolean
          ) => {
            switch (status) {
              case 'scheduled':
                return isMobile ? 'Status: SCHEDULED' : 'SCHEDULED'
              case 'active':
                return isMobile ? 'Status: ACTIVE' : 'ACTIVE'
              case 'expired':
                return isMobile ? 'Status: EXPIRED' : 'EXPIRED'
              default:
                return ''
            }
          }

          const status = calculatePromotionStatus(startDate, endDate)
          return promoStatusLabel(status, isMobile)
        },
        renderCell: (params) => {
          const status = params.value
          const promoBackgroundColor: Record<string, string> = {
            ACTIVE: Colors.promotionsStatus.background.Active,
            EXPIRED: Colors.promotionsStatus.background.Expired,
            SCHEDULED: Colors.promotionsStatus.background.Scheduled,
          }

          return (
            <Box
              style={{
                backgroundColor: promoBackgroundColor[status],
                color: colorPrimitives.black,
                fontWeight: 'bold',
                borderRadius: '4px',
                padding: '6px',
                display: 'inline-block',
              }}
            >
              {status}
            </Box>
          )
        },
      },
      {
        field: 'type',
        headerName: 'Type',
        minWidth: 215,
        flex: 1,
        valueGetter: (params) => {
          if (params.row.type === 'Global') {
            return 'J&J Promo'
          } else if (params.row.type === 'Location') {
            return params.row.licensedEstablishments.length === 1
              ? `${String(
                  params.row.licensedEstablishments?.[0]?.standardName
                )}`
              : `${String(
                  params.row.licensedEstablishments.length
                )} Location(s)`
          } else if (params.row.type === 'CorporateAccount') {
            const corporateAccount = params.row.corporateAccount
              ? params.row.corporateAccount
              : null
            if (corporateAccount) return corporateAccount.name
          } else if (params.row.type === 'Organization') {
            const organization = params.row.organization
              ? params.row.organization
              : null
            if (organization) {
              return organization.name
            }
          }
        },
      },
    ],
    [isMobile, navigate]
  )

  // Get LE Ids for the permission the user has access to
  const leIdsByPermission: number[] = getLEIdsByPermission(
    meQuery.data,
    selectedCorporateAccountId,
    'promotionsPermission'
  )

  // Filter data by permission
  const licensedEstablishmentsFiltered = promotionsQuery.data?.filter(
    (x) =>
      leIdsByPermission.includes(
        Number(x.licensedEstablishments?.[0]?.licensedEstablishmentId)
      ) || x.licensedEstablishments?.length === 0
  )

  const [filteredData, setFilteredData] = useState<Promotion[]>()

  const addchangeSpecialsEvents = useSetContactUsFields(
    (state) => state.addchangeSpecialsEvents
  )

  const handleFilteredData = (filteredData: Promotion[]) => {
    setFilteredData(filteredData)
  }

  const selectCell: GridEventListener<'cellClick'> = (params) => {
    if (params.field !== '__menu__' && params.id !== null) {
      void navigate(`/Specials&Events/${params.id}`)
    }
  }

  const sortedPromotions = useMemo(() => {
    if (!licensedEstablishmentsFiltered) return []

    // Create a shallow copy of the promotions array
    const promotionsCopy = [...licensedEstablishmentsFiltered]

    // Sort the copied array
    return promotionsCopy.sort((a, b) => {
      if (!a.startDate && !b.startDate) return 0
      if (!a.startDate) return -1
      if (!b.startDate) return 1

      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    })
  }, [licensedEstablishmentsFiltered])

  return (
    <Page
      header={
        <Box>
          <PageHeader
            title="Specials & Events"
            isSecondary={true}
            headerBgColor="white"
          />
        </Box>
      }
    >
      {meQuery.isPending && <ActivityIndicator />}
      {meQuery.isError && <ErrorIndicator />}
      {leIdsByPermission.length === 0 ? (
        <NoDataIndicator
          noImage
          noDataMessage="Your current permissions do not allow access to view specials & events. To obtain access, please contact your Account Manager for assistance."
        />
      ) : (
        <>
          <Stack
            direction={{ md: 'row', sm: 'column', xs: 'column' }}
            gap={{ md: 0, sm: 2, xs: 2 }}
            justifyContent={'space-between'}
          >
            <Box
              className="pb-8"
              sx={{ width: ['100%', '75%', '50%'], pb: ['0px'] }}
            >
              <GridSearchField<'FullPromotionDTO'>
                placeholder="Search Specials & Events"
                data={sortedPromotions}
                handleFilteredData={handleFilteredData}
                fullWidth
              />
            </Box>
            <div hidden={!contactUsPermission}>
              <Button
                sx={{ mb: ['20px'], pb: ['0px'] }}
                variant="contained"
                onClick={async () => {
                  addchangeSpecialsEvents()
                  await navigate('/ContactUs')
                }}
              >
                <Typography>Add/Change Specials/Events</Typography>
              </Button>
            </div>
          </Stack>

          {promotionsQuery.isPending && <ActivityIndicator />}
          {promotionsQuery.isError && <p>An error occured.</p>}
          {!promotionsQuery.isError && promotionsQuery.data && (
            <DataTable
              columns={columns}
              rows={filteredData ?? sortedPromotions}
              onCellClick={selectCell}
              onRowClick={selectCell}
            />
          )}
        </>
      )}
    </Page>
  )
}
